import Chip from '@components/v2/ui/Chip/Chip';
import Link from '@components/v2/ui/Link/Link';
import Typography from '@components/v2/ui/Typography/Typography';
import { Divider, Stack } from '@mui/material';
import LangEnum from '../../../../../../../../../lang/LangEnum';
import { OrderObjectTargetsTestIds } from '../../../../../../../../../utils/testIds/OrderObject.testIds';
import EmphasizedValueLabel from '../../../../../../../../target/form/components/EmphasizedValueLabel';
import { useTranslation } from '../../../../../../../../translation/TranslationContext';
import { OrderTargetSummaryView } from '../orderTargetSummaryTooltip.types';
import OrderTargetSummarySection from './OrderTargetSummarySection';

const CARD_MAX_WIDTH = 380;

const EMPHASIZED_SX = { fontWeight: 'medium' };

interface OrderTargetSummaryCardProps {
  summary: OrderTargetSummaryView;
}

const OrderTargetSummaryCard = ({ summary }: OrderTargetSummaryCardProps) => {
  const { translate } = useTranslation();

  const criterionChips = summary.criteria.map(({ criterion, values }) => (
    <Chip
      key={criterion}
      size="small"
      title={translate(LangEnum.TARGET_SUMMARY_CRITERION, { criterion, values })}
      label={
        <EmphasizedValueLabel
          labelKey={LangEnum.TARGET_SUMMARY_CRITERION}
          valueName="values"
          value={values}
          params={{ criterion }}
        />
      }
    />
  ));

  const dimensionChips = summary.dimensions.map((dimension) => (
    <Chip
      key={dimension}
      size="small"
      title={dimension}
      label={
        <Typography variant="inherit" component="span" sx={EMPHASIZED_SX}>
          {dimension}
        </Typography>
      }
    />
  ));

  return (
    <Stack
      test-id={OrderObjectTargetsTestIds.SUMMARY_CARD}
      sx={{ gap: 1.5, maxWidth: CARD_MAX_WIDTH, py: 0.5 }}
    >
      <Stack sx={{ gap: 0.25 }}>
        <Typography variant="micro1" sx={{ color: 'text.secondary' }}>
          {`${translate(LangEnum.GLOBAL_PLAN)} ${summary.code}`}
        </Typography>
        <Typography variant="body2" sx={EMPHASIZED_SX}>
          {summary.name}
        </Typography>
      </Stack>

      <Divider />

      <Typography variant="body2">
        <EmphasizedValueLabel
          labelKey={LangEnum.TARGET_SUMMARY_PERIOD}
          valueName="period"
          value={summary.period}
        />
      </Typography>

      <OrderTargetSummarySection label={LangEnum.GLOBAL_CRITERIA} chips={criterionChips} />
      <OrderTargetSummarySection label={LangEnum.GLOBAL_DIMENSIONS} chips={dimensionChips} />

      <Link
        to={summary.href}
        testId={OrderObjectTargetsTestIds.SUMMARY_LINK}
        sx={{ alignSelf: 'flex-start' }}
      >
        <Typography variant="body2" component="span" color="inherit">
          {translate(LangEnum.GLOBAL_MORE_DETAILS)}
        </Typography>
      </Link>
    </Stack>
  );
};

export default OrderTargetSummaryCard;
